/**
 * Reddit Ads — Metric & Dimension Catalogue (Story 19.11, Task 5)
 *
 * Single source of truth for the catalogue: 119 explicit primary metric rows
 * (from `_bmad/bmm/data/integration-metrics/reddit-ads.md` rows 1-121, where
 * rows 91 and 92 are template patterns expanded into 40 slot fields below) +
 * 40 custom-event slot expansions = 159 catalogue entries.
 *
 * (Note re: directive math — the directive lists 121 as-numbered rows; 2 of
 * those are the templates `CONVERSION_CUSTOM_EVENT_{N}_CLICKS` and
 * `CONVERSION_CUSTOM_EVENT_{N}_VIEWS` for N=1..20. Materialised catalogue:
 *   119 non-template + 40 slot expansions = 159 entries.
 * 21 dimensions follow the directive verbatim.)
 *
 * The `apiField` column is the **single source of truth** for which UPPERCASE
 * Reddit field names are valid in `body.fields[]`. The closed-set type
 * `RedditAdsApiField` is derived at compile-time from this catalogue.
 *
 * Reddit currency footgun: dual divisors documented per-entry.
 *   - micro÷1,000,000: SPEND, CPC, CPV, ECPM, *_ECPA, COST_PER_*_VIEW,
 *     APP_INSTALL_*_ECPA, APP_INSTALL_SKAN_*_ECPI, KEY_CONVERSION_ECPA
 *   - cents÷100:       CONVERSION_*_TOTAL_VALUE, CONVERSION_*_AVG_VALUE,
 *                      CONVERSION_SIGNUP_*_VALUE
 *
 * Cross-reference: directive lines 354-356 + Story 19.11 AC #5, #6, #16.
 */

import type {
  DimensionDefinition,
  MetricDefinition,
} from "../adapter";

// ============================================================================
// Constants
// ============================================================================

/** Number of custom event slots Reddit supports (`CONVERSION_CUSTOM_EVENT_{N}`). */
export const REDDIT_ADS_CUSTOM_EVENT_SLOTS = 20 as const;

// ============================================================================
// Helpers
// ============================================================================

const W_KPI: string[] = ["kpi"];
const W_KPI_LINE: string[] = ["kpi", "line-chart"];
const W_KPI_BAR_LINE_TABLE: string[] = ["kpi", "line-chart", "bar-chart", "data-table"];
const W_KPI_BAR_LINE: string[] = ["kpi", "line-chart", "bar-chart"];
const W_KPI_GAUGE: string[] = ["kpi", "gauge"];

const MICRO_DESC =
  "Returned in advertiser currency (Reddit returns this as micro-currency divided by 1,000,000 client-side).";
const CENTS_DESC =
  "Returned in advertiser currency (Reddit returns this as cents — divided by 100 client-side). Distinct from spend/CPC/eCPA fields which use micro-currency (÷1,000,000) — DO NOT confuse the divisors.";
const PERCENT_DESC =
  "Reddit returns this as a fraction in [0, 1]. Defensive normalization treats >1 values as display-percent and divides by 100.";
const REACH_FLOOR_DESC =
  "Available from June 2024 forward only — campaigns or queries spanning earlier dates will return null for this field. Adapter clamps starts_at to 2024-06-01 with a warning.";
const DERIVED_DESC =
  "Derived/computed metric — Reddit returns this server-side; do NOT calculate client-side as `clicks/impressions` etc. because Reddit's sampling and finalization windows differ from raw counter division.";

function M(
  key: string,
  label: string,
  type: MetricDefinition["type"],
  category: string,
  dataType: NonNullable<MetricDefinition["dataType"]>,
  compatibleWidgets: string[],
  apiField: string,
  description: string
): MetricDefinition {
  return {
    key,
    label,
    type,
    category,
    dataType,
    compatibleWidgets,
    apiField,
    description,
  };
}

// ============================================================================
// Conversion event family helpers (Purchase / AddToCart / AddToWishlist / ...)
// ============================================================================

interface ConversionFamily {
  /** UPPERCASE Reddit base, e.g. "PURCHASE", "ADD_TO_CART" */
  readonly base: string;
  /** camelCase prefix in our catalogue, e.g. "purchase", "addToCart" */
  readonly camel: string;
  /** Human-readable label, e.g. "Purchase", "Add to Cart" */
  readonly label: string;
  /** Category key, e.g. "conversion_purchase" */
  readonly category: string;
  /** Set if the family has a `_TOTAL_ITEMS` field. */
  readonly hasTotalItems?: boolean;
  /** Set if the family has `_AVG_VALUE` and `_TOTAL_VALUE` fields. */
  readonly hasValueFields?: boolean;
  /** Set if the family has a `_CONVERSION_RATE` field. */
  readonly hasConversionRate?: boolean;
}

const PURCHASE_FAMILY: ConversionFamily = {
  base: "PURCHASE",
  camel: "purchase",
  label: "Purchase",
  category: "conversion_purchase",
  hasTotalItems: true,
  hasValueFields: true,
  hasConversionRate: true,
};
const ADD_TO_CART_FAMILY: ConversionFamily = {
  base: "ADD_TO_CART",
  camel: "addToCart",
  label: "Add to Cart",
  category: "conversion_addToCart",
  hasTotalItems: true,
  hasValueFields: true,
};
const ADD_TO_WISHLIST_FAMILY: ConversionFamily = {
  base: "ADD_TO_WISHLIST",
  camel: "addToWishlist",
  label: "Add to Wishlist",
  category: "conversion_addToWishlist",
  hasTotalItems: true,
  hasValueFields: true,
};
const LEAD_FAMILY: ConversionFamily = {
  base: "LEAD",
  camel: "lead",
  label: "Lead",
  category: "conversion_lead",
  hasValueFields: true,
};

function buildConversionFamilyMetrics(f: ConversionFamily): MetricDefinition[] {
  const widgets = W_KPI_LINE;
  const entries: MetricDefinition[] = [
    M(
      `${f.camel}Clicks`,
      `${f.label} Clicks`,
      "number",
      f.category,
      "Integer",
      widgets,
      `CONVERSION_${f.base}_CLICKS`,
      `Click-attributed ${f.label.toLowerCase()} events. EXCLUDES view-through — see ${f.label} Views for those, or ${f.label} Total Count for the attribution-blind sum. Use the Click Attribution Window dimension to compare 1d/7d/28d windows.`
    ),
    M(
      `${f.camel}Views`,
      `${f.label} Views`,
      "number",
      f.category,
      "Integer",
      widgets,
      `CONVERSION_${f.base}_VIEWS`,
      `View-through-attributed ${f.label.toLowerCase()} events. Counts conversions from users who saw (but did not click) the ad. Use the View Attribution Window dimension to filter.`
    ),
    M(
      `${f.camel}Ecpa`,
      `${f.label} eCPA`,
      "currency",
      f.category,
      "Currency",
      W_KPI,
      `CONVERSION_${f.base}_ECPA`,
      `Effective cost per ${f.label.toLowerCase()} event. ${MICRO_DESC}`
    ),
  ];
  if (f.hasValueFields) {
    entries.push(
      M(
        `${f.camel}AvgValue`,
        `${f.label} Avg Value`,
        "currency",
        f.category,
        "Currency",
        W_KPI,
        `CONVERSION_${f.base}_AVG_VALUE`,
        `Average value per ${f.label.toLowerCase()} event. ${CENTS_DESC}`
      ),
      M(
        `${f.camel}TotalValue`,
        `${f.label} Total Value`,
        "currency",
        f.category,
        "Currency",
        W_KPI_LINE,
        `CONVERSION_${f.base}_TOTAL_VALUE`,
        `Total value across all ${f.label.toLowerCase()} events. ${CENTS_DESC}`
      )
    );
  }
  if (f.hasTotalItems) {
    entries.push(
      M(
        `${f.camel}TotalItems`,
        `${f.label} Total Items`,
        "number",
        f.category,
        "Integer",
        W_KPI,
        `CONVERSION_${f.base}_TOTAL_ITEMS`,
        `Total items across all ${f.label.toLowerCase()} events.`
      )
    );
  }
  // Total Count (always)
  entries.push(
    M(
      `${f.camel}TotalCount`,
      `${f.label} Total Count`,
      "number",
      f.category,
      "Integer",
      W_KPI,
      `CONVERSION_${f.base}_TOTAL_COUNT`,
      `Total ${f.label.toLowerCase()} count (clicks + views), ignoring attribution-window dimension.`
    )
  );
  if (f.hasConversionRate) {
    entries.push(
      M(
        `${f.camel}ConversionRate`,
        `${f.label} Conversion Rate`,
        "percentage",
        f.category,
        "Percentage",
        W_KPI_GAUGE,
        `CONVERSION_${f.base}_CONVERSION_RATE`,
        `${f.label} conversion rate (server-computed). ${DERIVED_DESC} ${PERCENT_DESC}`
      )
    );
  }
  return entries;
}

// Smaller conversion family (Sign Up — only Clicks/Views/eCPA/TotalCount = 4)
function buildSignUpMetrics(): MetricDefinition[] {
  return [
    M(
      "signUpClicks",
      "Sign Up Clicks",
      "number",
      "conversion_signUp",
      "Integer",
      W_KPI_LINE,
      "CONVERSION_SIGN_UP_CLICKS",
      "Click-attributed sign-up events. EXCLUDES view-through."
    ),
    M(
      "signUpViews",
      "Sign Up Views",
      "number",
      "conversion_signUp",
      "Integer",
      W_KPI,
      "CONVERSION_SIGN_UP_VIEWS",
      "View-through-attributed sign-up events."
    ),
    M(
      "signUpEcpa",
      "Sign Up eCPA",
      "currency",
      "conversion_signUp",
      "Currency",
      W_KPI,
      "CONVERSION_SIGN_UP_ECPA",
      `Effective cost per sign-up event. ${MICRO_DESC}`
    ),
    M(
      "signUpTotalCount",
      "Sign Up Total Count",
      "number",
      "conversion_signUp",
      "Integer",
      W_KPI,
      "CONVERSION_SIGN_UP_TOTAL_COUNT",
      "Total sign-up count across all attributions."
    ),
  ];
}

// Signup Legacy value fields (2 entries — older `SIGNUP` no-underscore variant)
function buildSignupLegacyMetrics(): MetricDefinition[] {
  return [
    M(
      "signupAvgValue",
      "Signup Avg Value (legacy)",
      "currency",
      "conversion_signUp",
      "Currency",
      W_KPI,
      "CONVERSION_SIGNUP_AVG_VALUE",
      `Legacy signup avg value (older field, persists alongside _SIGN_UP). ${CENTS_DESC}`
    ),
    M(
      "signupTotalValue",
      "Signup Total Value (legacy)",
      "currency",
      "conversion_signUp",
      "Currency",
      W_KPI,
      "CONVERSION_SIGNUP_TOTAL_VALUE",
      `Legacy signup total value (older field, persists alongside _SIGN_UP). ${CENTS_DESC}`
    ),
  ];
}

// Smaller conversion family without value fields — Page Visit, View Content, Search (each: Clicks/Views/eCPA/TotalCount = 4)
function buildSmallConversionFamilyMetrics(
  family: { base: string; camel: string; label: string; category: string }
): MetricDefinition[] {
  return [
    M(
      `${family.camel}Clicks`,
      `${family.label} Clicks`,
      "number",
      family.category,
      "Integer",
      W_KPI,
      `CONVERSION_${family.base}_CLICKS`,
      `Click-attributed ${family.label.toLowerCase()} events.`
    ),
    M(
      `${family.camel}Views`,
      `${family.label} Views`,
      "number",
      family.category,
      "Integer",
      W_KPI,
      `CONVERSION_${family.base}_VIEWS`,
      `View-through-attributed ${family.label.toLowerCase()} events.`
    ),
    M(
      `${family.camel}Ecpa`,
      `${family.label} eCPA`,
      "currency",
      family.category,
      "Currency",
      W_KPI,
      `CONVERSION_${family.base}_ECPA`,
      `Effective cost per ${family.label.toLowerCase()} event. ${MICRO_DESC}`
    ),
    M(
      `${family.camel}TotalCount`,
      `${family.label} Total Count`,
      "number",
      family.category,
      "Integer",
      W_KPI,
      `CONVERSION_${family.base}_TOTAL_COUNT`,
      `Total ${family.label.toLowerCase()} count across all attributions.`
    ),
  ];
}

// ============================================================================
// Custom Event slot expansion (templates 91, 92 → 40 slot fields)
// ============================================================================

function buildCustomEventSlotMetrics(): MetricDefinition[] {
  const entries: MetricDefinition[] = [];
  for (let n = 1; n <= REDDIT_ADS_CUSTOM_EVENT_SLOTS; n++) {
    entries.push(
      M(
        `customEvent${n}Clicks`,
        `Custom Event ${n} Clicks`,
        "number",
        "conversion_custom",
        "Integer",
        W_KPI,
        `CONVERSION_CUSTOM_EVENT_${n}_CLICKS`,
        `Click-attributed custom-event slot ${n} (account-specific label — see metadata.customEventLabels).`
      ),
      M(
        `customEvent${n}Views`,
        `Custom Event ${n} Views`,
        "number",
        "conversion_custom",
        "Integer",
        W_KPI,
        `CONVERSION_CUSTOM_EVENT_${n}_VIEWS`,
        `View-through-attributed custom-event slot ${n} (account-specific label — see metadata.customEventLabels).`
      )
    );
  }
  return entries;
}

// ============================================================================
// REDDIT_ADS_METRICS — full catalogue
// ============================================================================

const REDDIT_ADS_METRICS_BASE: MetricDefinition[] = [
  // Core Delivery (8) — directive rows 1-8
  M("impressions", "Impressions", "number", "delivery", "Integer", W_KPI_BAR_LINE_TABLE, "IMPRESSIONS", "Total ad impressions."),
  M("reach", "Reach", "number", "delivery", "Integer", W_KPI, "REACH", `Unique users reached. ${REACH_FLOOR_DESC}`),
  M("frequency", "Frequency", "number", "delivery", "Decimal", W_KPI_GAUGE, "FREQUENCY", `Average impressions per unique user. ${REACH_FLOOR_DESC}`),
  M("clicks", "Clicks", "number", "delivery", "Integer", W_KPI_BAR_LINE, "CLICKS", "Total ad clicks."),
  M("ctr", "CTR", "percentage", "delivery", "Percentage", W_KPI_GAUGE, "CTR", `Click-through rate. ${DERIVED_DESC} ${PERCENT_DESC}`),
  M("engagedClick", "Engaged Clicks", "number", "delivery", "Integer", W_KPI, "ENGAGED_CLICK", "Clicks that led to deeper engagement (long dwell, scroll, etc.)."),
  M("redditLeads", "Reddit Leads", "number", "delivery", "Integer", W_KPI_LINE, "REDDIT_LEADS", "Reddit-form Lead conversions (Reddit's native lead-gen form, distinct from Conversion API leads)."),
  M("priority", "Priority", "number", "delivery", "Integer", W_KPI, "PRIORITY", "Ad priority indicator from Reddit's auction layer."),

  // Spend & Cost Efficiency (5) — directive rows 9-13
  M("spend", "Spend", "currency", "spend", "Currency", W_KPI_BAR_LINE, "SPEND", `Total spend. ${MICRO_DESC}`),
  M("cpc", "CPC", "currency", "spend", "Currency", W_KPI, "CPC", `Cost per click. ${DERIVED_DESC} ${MICRO_DESC}`),
  M("cpv", "CPV", "currency", "spend", "Currency", W_KPI, "CPV", `Cost per view. ${DERIVED_DESC} ${MICRO_DESC}`),
  M("ecpm", "ECPM", "currency", "spend", "Currency", W_KPI, "ECPM", `Effective cost per mille. ${DERIVED_DESC} ${MICRO_DESC}`),
  M("roas", "ROAS", "number", "spend", "Ratio", W_KPI_GAUGE, "ROAS", `Return on ad spend. ${DERIVED_DESC} Computed by Reddit as conversion-value ÷ spend.`),

  // Key Conversion (5) — directive rows 14-18
  M("keyConversionTotalCount", "Key Conversion Total Count", "number", "key_conversion", "Integer", W_KPI_LINE, "KEY_CONVERSION_TOTAL_COUNT", "Total count of the campaign's designated primary conversion event."),
  M("keyConversionClicks", "Key Conversion Clicks", "number", "key_conversion", "Integer", W_KPI, "KEY_CONVERSION_CLICKS", "Click-attributed key conversions."),
  M("keyConversionViews", "Key Conversion Views", "number", "key_conversion", "Integer", W_KPI, "KEY_CONVERSION_VIEWS", "View-attributed key conversions."),
  M("keyConversionRate", "Key Conversion Rate", "percentage", "key_conversion", "Percentage", W_KPI_GAUGE, "KEY_CONVERSION_RATE", `Key conversion rate. ${DERIVED_DESC} ${PERCENT_DESC}`),
  M("keyConversionEcpa", "Key Conversion eCPA", "currency", "key_conversion", "Currency", W_KPI, "KEY_CONVERSION_ECPA", `Effective cost per key conversion. ${MICRO_DESC}`),

  // Video — Main (15) — directive rows 19-33
  M("videoStarted", "Video Started", "number", "video_main", "Integer", W_KPI_LINE, "VIDEO_STARTED", "Video impressions where playback was initiated."),
  M("videoWatched3Seconds", "Video Watched 3 Seconds", "number", "video_main", "Integer", W_KPI, "VIDEO_WATCHED_3_SECONDS", "Video plays of at least 3 seconds."),
  M("videoWatched5Seconds", "Video Watched 5 Seconds", "number", "video_main", "Integer", W_KPI, "VIDEO_WATCHED_5_SECONDS", "Video plays of at least 5 seconds."),
  M("videoWatched10Seconds", "Video Watched 10 Seconds", "number", "video_main", "Integer", W_KPI, "VIDEO_WATCHED_10_SECONDS", "Video plays of at least 10 seconds."),
  M("videoWatched25Percent", "Video Watched 25%", "number", "video_main", "Integer", W_KPI, "VIDEO_WATCHED_25_PERCENT", "Video plays that reached the 25% mark."),
  M("videoWatched50Percent", "Video Watched 50%", "number", "video_main", "Integer", W_KPI, "VIDEO_WATCHED_50_PERCENT", "Video plays that reached the 50% mark."),
  M("videoWatched75Percent", "Video Watched 75%", "number", "video_main", "Integer", W_KPI, "VIDEO_WATCHED_75_PERCENT", "Video plays that reached the 75% mark."),
  M("videoWatched95Percent", "Video Watched 95%", "number", "video_main", "Integer", W_KPI, "VIDEO_WATCHED_95_PERCENT", "Video plays that reached the 95% mark."),
  M("videoWatched100Percent", "Video Watched 100%", "number", "video_main", "Integer", W_KPI, "VIDEO_WATCHED_100_PERCENT", "Completed video plays."),
  M("videoViewableImpressions", "Video Viewable Impressions", "number", "video_main", "Integer", W_KPI, "VIDEO_VIEWABLE_IMPRESSIONS", "Video impressions that met MRC viewability standards (≥50% on screen for ≥2s)."),
  M("videoFullyViewableImpressions", "Video Fully Viewable Impressions", "number", "video_main", "Integer", W_KPI, "VIDEO_FULLY_VIEWABLE_IMPRESSIONS", "Video impressions that met fully-viewable standards (100% on screen)."),
  M("videoCompletionRate", "Video Completion Rate", "percentage", "video_main", "Percentage", W_KPI_GAUGE, "VIDEO_COMPLETION_RATE", `Video completion rate. ${DERIVED_DESC} ${PERCENT_DESC}`),
  M("videoViewRate", "Video View Rate", "percentage", "video_main", "Percentage", W_KPI_GAUGE, "VIDEO_VIEW_RATE", `Video view rate (overall). ${DERIVED_DESC} ${PERCENT_DESC}`),
  M("videoViewRate6Seconds", "Video View Rate @ 6 Seconds", "percentage", "video_main", "Percentage", W_KPI_GAUGE, "VIDEO_VIEW_RATE_6_SECONDS", `Video view rate @ 6 seconds. ${DERIVED_DESC} ${PERCENT_DESC}`),
  M("videoViewRate15Seconds", "Video View Rate @ 15 Seconds", "percentage", "video_main", "Percentage", W_KPI_GAUGE, "VIDEO_VIEW_RATE_15_SECONDS", `Video view rate @ 15 seconds. ${DERIVED_DESC} ${PERCENT_DESC}`),

  // Video — Attachment (8) — directive rows 34-41
  M("videoPlaysExpanded", "Video Plays Expanded", "number", "video_attachment", "Integer", W_KPI, "VIDEO_PLAYS_EXPANDED", "Video plays where the user expanded to fullscreen."),
  M("videoPlaysWithSound", "Video Plays With Sound", "number", "video_attachment", "Integer", W_KPI, "VIDEO_PLAYS_WITH_SOUND", "Video plays with sound enabled (user-initiated unmute)."),
  M("videoViewableWatched6Seconds", "Video Viewable Watched 6s", "number", "video_attachment", "Integer", W_KPI, "VIDEO_VIEWABLE_WATCHED_6_SECONDS", "Viewable video impressions of at least 6 seconds."),
  M("videoViewableWatched15Seconds", "Video Viewable Watched 15s", "number", "video_attachment", "Integer", W_KPI, "VIDEO_VIEWABLE_WATCHED_15_SECONDS", "Viewable video impressions of at least 15 seconds."),
  M("costPer3SecondView", "Cost Per 3-Second View", "currency", "video_attachment", "Currency", W_KPI, "COST_PER_3_SECOND_VIEW", `Effective cost per 3-second video view. ${MICRO_DESC}`),
  M("costPer6SecondView", "Cost Per 6-Second View", "currency", "video_attachment", "Currency", W_KPI, "COST_PER_6_SECOND_VIEW", `Effective cost per 6-second video view. ${MICRO_DESC}`),
  M("costPer15SecondView", "Cost Per 15-Second View", "currency", "video_attachment", "Currency", W_KPI, "COST_PER_15_SECOND_VIEW", `Effective cost per 15-second video view. ${MICRO_DESC}`),
  M("costPerCompletedView", "Cost Per Completed View", "currency", "video_attachment", "Currency", W_KPI, "COST_PER_COMPLETED_VIEW", `Effective cost per completed video view. ${MICRO_DESC}`),

  // Conversions — Purchase (8) — directive rows 42-49
  ...buildConversionFamilyMetrics(PURCHASE_FAMILY),

  // Conversions — Add to Cart (7) — directive rows 50-56
  ...buildConversionFamilyMetrics(ADD_TO_CART_FAMILY),

  // Conversions — Add to Wishlist (7) — directive rows 57-63
  ...buildConversionFamilyMetrics(ADD_TO_WISHLIST_FAMILY),

  // Conversions — Lead (6) — directive rows 64-69
  ...buildConversionFamilyMetrics(LEAD_FAMILY),

  // Conversions — Sign Up (4) — directive rows 70-73
  ...buildSignUpMetrics(),

  // Conversions — Signup Legacy (2) — directive rows 74-75
  ...buildSignupLegacyMetrics(),

  // Conversions — Page Visit (4) — directive rows 76-79
  ...buildSmallConversionFamilyMetrics({
    base: "PAGE_VISIT",
    camel: "pageVisit",
    label: "Page Visit",
    category: "conversion_pageVisit",
  }),

  // Conversions — View Content (4) — directive rows 80-83
  ...buildSmallConversionFamilyMetrics({
    base: "VIEW_CONTENT",
    camel: "viewContent",
    label: "View Content",
    category: "conversion_viewContent",
  }),

  // Conversions — Search (4) — directive rows 84-87
  ...buildSmallConversionFamilyMetrics({
    base: "SEARCH",
    camel: "search",
    label: "Search",
    category: "conversion_search",
  }),

  // Conversions — Custom (3 aggregates) — directive rows 88-90
  M(
    "customClicks",
    "Custom Clicks",
    "number",
    "conversion_custom",
    "Integer",
    W_KPI,
    "CONVERSION_CUSTOM_CLICKS",
    "Aggregate click-attributed custom-event count across all 20 slots."
  ),
  M(
    "customViews",
    "Custom Views",
    "number",
    "conversion_custom",
    "Integer",
    W_KPI,
    "CONVERSION_CUSTOM_VIEWS",
    "Aggregate view-attributed custom-event count across all 20 slots."
  ),
  M(
    "customEcpa",
    "Custom eCPA",
    "currency",
    "conversion_custom",
    "Currency",
    W_KPI,
    "CONVERSION_CUSTOM_ECPA",
    `Effective cost per custom conversion (aggregate). ${MICRO_DESC}`
  ),

  // Conversions — Custom Event slots 1..20 × CLICKS/VIEWS = 40 entries
  // (directive template rows 91, 92 — expanded materialised entries)
  ...buildCustomEventSlotMetrics(),

  // App Install — Regular (9) — directive rows 93-101
  M("appInstallCount", "App Install Count", "number", "app_install_regular", "Integer", W_KPI_LINE, "APP_INSTALL_COUNT", "App installs attributed to the campaign."),
  M("appInstallCvr", "App Install CVR", "percentage", "app_install_regular", "Percentage", W_KPI_GAUGE, "APP_INSTALL_CVR", `App install conversion rate. ${DERIVED_DESC} ${PERCENT_DESC}`),
  M("appInstallEcpa", "App Install eCPA", "currency", "app_install_regular", "Currency", W_KPI, "APP_INSTALL_ECPA", `Effective cost per app install. ${MICRO_DESC}`),
  M("appReinstallCount", "App Reinstall Count", "number", "app_install_regular", "Integer", W_KPI, "APP_REINSTALL_COUNT", "App reinstalls attributed to the campaign."),
  M("appLaunchCount", "App Launch Count", "number", "app_install_regular", "Integer", W_KPI, "APP_LAUNCH_COUNT", "Post-install app launches."),
  M("appAddPaymentInfoCount", "App Add Payment Info Count", "number", "app_install_regular", "Integer", W_KPI, "APP_ADD_PAYMENT_INFO_COUNT", "Post-install add-payment-info events."),
  M("appPurchaseCount", "App Purchase Count", "number", "app_install_regular", "Integer", W_KPI, "APP_PURCHASE_COUNT", "Post-install in-app purchases."),
  M("appSignUpCount", "App Sign Up Count", "number", "app_install_regular", "Integer", W_KPI, "APP_SIGN_UP_COUNT", "Post-install sign-up events."),
  M("appAddToCartCount", "App Add To Cart Count", "number", "app_install_regular", "Integer", W_KPI, "APP_ADD_TO_CART_COUNT", "Post-install add-to-cart events."),

  // App Install — MMP (12) — directive rows 102-113
  M("appInstallMmpCount", "MMP App Installs", "number", "app_install_mmp", "Integer", W_KPI, "APP_INSTALL_MMP_COUNT", "MMP-attributed app installs (third-party Mobile Measurement Partner)."),
  M("appLaunchMmpCount", "MMP App Launches", "number", "app_install_mmp", "Integer", W_KPI, "APP_LAUNCH_MMP_COUNT", "MMP-attributed app launches."),
  M("appSignUpMmpCount", "MMP App Sign Ups", "number", "app_install_mmp", "Integer", W_KPI, "APP_SIGN_UP_MMP_COUNT", "MMP-attributed app sign-ups."),
  M("appPurchaseMmpCount", "MMP App Purchases", "number", "app_install_mmp", "Integer", W_KPI, "APP_PURCHASE_MMP_COUNT", "MMP-attributed in-app purchases."),
  M("appAddToCartMmpCount", "MMP App Add to Cart", "number", "app_install_mmp", "Integer", W_KPI, "APP_ADD_TO_CART_MMP_COUNT", "MMP-attributed add-to-cart events."),
  M("appViewContentMmpCount", "MMP App View Content", "number", "app_install_mmp", "Integer", W_KPI, "APP_VIEW_CONTENT_MMP_COUNT", "MMP-attributed view-content events."),
  M("appLevelAchievedMmpCount", "MMP App Level Achieved", "number", "app_install_mmp", "Integer", W_KPI, "APP_LEVEL_ACHIEVED_MMP_COUNT", "MMP-attributed level-achieved events."),
  M("appCompletedTutorialMmpCount", "MMP App Completed Tutorial", "number", "app_install_mmp", "Integer", W_KPI, "APP_COMPLETED_TUTORIAL_MMP_COUNT", "MMP-attributed tutorial-complete events."),
  M("appSpendCreditsMmpCount", "MMP App Spend Credits", "number", "app_install_mmp", "Integer", W_KPI, "APP_SPEND_CREDITS_MMP_COUNT", "MMP-attributed spend-credits events."),
  M("appSearchMmpCount", "MMP App Searches", "number", "app_install_mmp", "Integer", W_KPI, "APP_SEARCH_MMP_COUNT", "MMP-attributed search events."),
  M("appAddPaymentInfoMmpCount", "MMP App Add Payment Info", "number", "app_install_mmp", "Integer", W_KPI, "APP_ADD_PAYMENT_INFO_MMP_COUNT", "MMP-attributed add-payment-info events."),
  M("appInstallMmpEcpa", "MMP App Install eCPA", "currency", "app_install_mmp", "Currency", W_KPI, "APP_INSTALL_MMP_ECPA", `Effective cost per MMP-attributed install. ${MICRO_DESC}`),

  // App Install — SKAN (8) — directive rows 114-121
  M("appInstallSkanCount", "SKAN App Installs", "number", "app_install_skan", "Integer", W_KPI, "APP_INSTALL_SKAN_COUNT", "Apple SKAdNetwork-attributed app installs."),
  M("appRedownloadSkanCount", "SKAN Redownloads", "number", "app_install_skan", "Integer", W_KPI, "APP_REDOWNLOAD_SKAN_COUNT", "Apple SKAdNetwork-attributed app redownloads."),
  M("appInstallSkanTotalCount", "SKAN Total App Installs", "number", "app_install_skan", "Integer", W_KPI, "APP_INSTALL_SKAN_TOTAL_COUNT", "Total Apple SKAdNetwork-attributed installs (installs + redownloads)."),
  M("appInstallSkanEcpi", "SKAN Install eCPI", "currency", "app_install_skan", "Currency", W_KPI, "APP_INSTALL_SKAN_ECPI", `Effective cost per SKAN install. ${MICRO_DESC}`),
  M("appRedownloadSkanEcpi", "SKAN Redownload eCPI", "currency", "app_install_skan", "Currency", W_KPI, "APP_REDOWNLOAD_SKAN_ECPI", `Effective cost per SKAN redownload. ${MICRO_DESC}`),
  M("appInstallSkanTotalEcpi", "SKAN Total App Install eCPI", "currency", "app_install_skan", "Currency", W_KPI, "APP_INSTALL_SKAN_TOTAL_ECPI", `Effective cost per total SKAN install. ${MICRO_DESC}`),
  M("appInstallSkanCvr", "SKAN Install CVR", "percentage", "app_install_skan", "Percentage", W_KPI_GAUGE, "APP_INSTALL_SKAN_CVR", `SKAN install conversion rate. ${DERIVED_DESC} ${PERCENT_DESC}`),
  M("appInstallSkanTotalCvr", "SKAN Total App Install CVR", "percentage", "app_install_skan", "Percentage", W_KPI_GAUGE, "APP_INSTALL_SKAN_TOTAL_CVR", `Total SKAN install conversion rate. ${DERIVED_DESC} ${PERCENT_DESC}`),
];

// ============================================================================
// Story 31-31 — aggregation annotations (multi-attachment widgets, ADR-0004)
// ============================================================================
// The positional `M(...)` builder cannot carry the new optional fields without
// churning every call signature, so annotations are composed AFTER the base
// catalogue (sanctioned by Story 31-31 AC #4: "extend the M(...) helper or
// compose after"). Every metric key MUST appear below — omissions fail the
// registry-wide gap report in `metric-registry.aggregation.test.ts`.
//   A  = additive (values sum across attached resources)
//   R  = ratio with the natural weight as denominator (weighted average)
//   RU = ratio with NO catalog weight -> UNWEIGHTED_RATIO_ALLOWLIST entry
type AggregationAnnotation = Pick<
  MetricDefinition,
  "aggregationKind" | "ratioComponents"
>;
const A: AggregationAnnotation = { aggregationKind: "additive" };
const RU: AggregationAnnotation = { aggregationKind: "ratio" };
const R = (denominator: string, numerator?: string): AggregationAnnotation => ({
  aggregationKind: "ratio",
  ratioComponents: {
    ...(numerator !== undefined ? { numerator } : {}),
    denominator,
  },
});

const REDDIT_ADS_METRICS_AGGREGATION: Readonly<Record<string, AggregationAnnotation>> = {
  impressions: A,
  reach: A,
  frequency: R("reach", "impressions"),
  clicks: A,
  ctr: R("impressions", "clicks"),
  engagedClick: A,
  redditLeads: A,
  priority: RU,
  spend: A,
  cpc: R("clicks", "spend"),
  cpv: R("videoStarted", "spend"),
  ecpm: R("impressions", "spend"),
  roas: R("spend"),
  keyConversionTotalCount: A,
  keyConversionClicks: A,
  keyConversionViews: A,
  keyConversionRate: R("clicks", "keyConversionTotalCount"),
  keyConversionEcpa: R("keyConversionTotalCount", "spend"),
  videoStarted: A,
  videoWatched3Seconds: A,
  videoWatched5Seconds: A,
  videoWatched10Seconds: A,
  videoWatched25Percent: A,
  videoWatched50Percent: A,
  videoWatched75Percent: A,
  videoWatched95Percent: A,
  videoWatched100Percent: A,
  videoViewableImpressions: A,
  videoFullyViewableImpressions: A,
  videoCompletionRate: R("videoStarted", "videoWatched100Percent"),
  videoViewRate: R("impressions", "videoStarted"),
  videoViewRate6Seconds: R("impressions", "videoViewableWatched6Seconds"),
  videoViewRate15Seconds: R("impressions", "videoViewableWatched15Seconds"),
  videoPlaysExpanded: A,
  videoPlaysWithSound: A,
  videoViewableWatched6Seconds: A,
  videoViewableWatched15Seconds: A,
  costPer3SecondView: R("videoWatched3Seconds", "spend"),
  costPer6SecondView: R("videoViewableWatched6Seconds", "spend"),
  costPer15SecondView: R("videoViewableWatched15Seconds", "spend"),
  costPerCompletedView: R("videoWatched100Percent", "spend"),
  purchaseClicks: A,
  purchaseViews: A,
  purchaseEcpa: R("purchaseTotalCount", "spend"),
  purchaseAvgValue: R("purchaseTotalCount"),
  purchaseTotalValue: A,
  purchaseTotalItems: A,
  purchaseTotalCount: A,
  purchaseConversionRate: R("clicks", "purchaseTotalCount"),
  addToCartClicks: A,
  addToCartViews: A,
  addToCartEcpa: R("addToCartTotalCount", "spend"),
  addToCartAvgValue: R("addToCartTotalCount"),
  addToCartTotalValue: A,
  addToCartTotalItems: A,
  addToCartTotalCount: A,
  addToWishlistClicks: A,
  addToWishlistViews: A,
  addToWishlistEcpa: R("addToWishlistTotalCount", "spend"),
  addToWishlistAvgValue: R("addToWishlistTotalCount"),
  addToWishlistTotalValue: A,
  addToWishlistTotalItems: A,
  addToWishlistTotalCount: A,
  leadClicks: A,
  leadViews: A,
  leadEcpa: R("leadTotalCount", "spend"),
  leadAvgValue: R("leadTotalCount"),
  leadTotalValue: A,
  leadTotalCount: A,
  signUpClicks: A,
  signUpViews: A,
  signUpEcpa: R("signUpTotalCount", "spend"),
  signUpTotalCount: A,
  signupAvgValue: R("signUpTotalCount"),
  signupTotalValue: A,
  pageVisitClicks: A,
  pageVisitViews: A,
  pageVisitEcpa: R("pageVisitTotalCount", "spend"),
  pageVisitTotalCount: A,
  viewContentClicks: A,
  viewContentViews: A,
  viewContentEcpa: R("viewContentTotalCount", "spend"),
  viewContentTotalCount: A,
  searchClicks: A,
  searchViews: A,
  searchEcpa: R("searchTotalCount", "spend"),
  searchTotalCount: A,
  customClicks: A,
  customViews: A,
  customEcpa: RU,
  customEvent1Clicks: A,
  customEvent1Views: A,
  customEvent2Clicks: A,
  customEvent2Views: A,
  customEvent3Clicks: A,
  customEvent3Views: A,
  customEvent4Clicks: A,
  customEvent4Views: A,
  customEvent5Clicks: A,
  customEvent5Views: A,
  customEvent6Clicks: A,
  customEvent6Views: A,
  customEvent7Clicks: A,
  customEvent7Views: A,
  customEvent8Clicks: A,
  customEvent8Views: A,
  customEvent9Clicks: A,
  customEvent9Views: A,
  customEvent10Clicks: A,
  customEvent10Views: A,
  customEvent11Clicks: A,
  customEvent11Views: A,
  customEvent12Clicks: A,
  customEvent12Views: A,
  customEvent13Clicks: A,
  customEvent13Views: A,
  customEvent14Clicks: A,
  customEvent14Views: A,
  customEvent15Clicks: A,
  customEvent15Views: A,
  customEvent16Clicks: A,
  customEvent16Views: A,
  customEvent17Clicks: A,
  customEvent17Views: A,
  customEvent18Clicks: A,
  customEvent18Views: A,
  customEvent19Clicks: A,
  customEvent19Views: A,
  customEvent20Clicks: A,
  customEvent20Views: A,
  appInstallCount: A,
  appInstallCvr: R("clicks", "appInstallCount"),
  appInstallEcpa: R("appInstallCount", "spend"),
  appReinstallCount: A,
  appLaunchCount: A,
  appAddPaymentInfoCount: A,
  appPurchaseCount: A,
  appSignUpCount: A,
  appAddToCartCount: A,
  appInstallMmpCount: A,
  appLaunchMmpCount: A,
  appSignUpMmpCount: A,
  appPurchaseMmpCount: A,
  appAddToCartMmpCount: A,
  appViewContentMmpCount: A,
  appLevelAchievedMmpCount: A,
  appCompletedTutorialMmpCount: A,
  appSpendCreditsMmpCount: A,
  appSearchMmpCount: A,
  appAddPaymentInfoMmpCount: A,
  appInstallMmpEcpa: R("appInstallMmpCount", "spend"),
  appInstallSkanCount: A,
  appRedownloadSkanCount: A,
  appInstallSkanTotalCount: A,
  appInstallSkanEcpi: R("appInstallSkanCount", "spend"),
  appRedownloadSkanEcpi: R("appRedownloadSkanCount", "spend"),
  appInstallSkanTotalEcpi: R("appInstallSkanTotalCount", "spend"),
  appInstallSkanCvr: R("clicks", "appInstallSkanCount"),
  appInstallSkanTotalCvr: R("clicks", "appInstallSkanTotalCount"),
};

export const REDDIT_ADS_METRICS: MetricDefinition[] = REDDIT_ADS_METRICS_BASE.map((m) => {
  const agg = REDDIT_ADS_METRICS_AGGREGATION[m.key];
  return agg ? { ...m, ...agg } : m;
});


// ============================================================================
// REDDIT_ADS_DIMENSIONS — 21 entries (directive Section "Dimensions" rows 1-21)
// ============================================================================

function D(
  key: string,
  label: string,
  type: DimensionDefinition["type"],
  exampleValues?: string[],
  deliveryOnlyWarning?: boolean
): DimensionDefinition {
  const def: DimensionDefinition = { key, label, type };
  if (exampleValues !== undefined) (def as { exampleValues?: string[] }).exampleValues = exampleValues;
  if (deliveryOnlyWarning) (def as { deliveryOnlyWarning?: boolean }).deliveryOnlyWarning = true;
  return def;
}

export const REDDIT_ADS_DIMENSIONS: DimensionDefinition[] = [
  // Structure (4)
  D("adAccountId", "Ad Account", "Structure", ["a2_abc123"]),
  D("campaignId", "Campaign", "Structure", ["t3_abc123"]),
  D("adGroupId", "Ad Group", "Structure", ["t6_xyz789"]),
  D("adId", "Ad", "Structure", ["t4_xyz789"]),
  // Time (2)
  D("date", "Date (daily)", "Time", ["2026-04-01", "2026-04-02"]),
  D("hour", "Hour", "Time", ["2026-04-01T09:00:00Z"]),
  // Geography (4)
  D("country", "Country", "Geo", ["US", "CA", "GB"]),
  D("region", "Region", "Geo", ["California", "Ontario"]),
  D("dma", "DMA", "Geo", ["501", "803"]),
  D("metro", "Metro", "Geo", ["New York", "Los Angeles"]),
  // Demographics (2)
  D("gender", "Gender", "Demo", ["MALE", "FEMALE"]),
  D("interest", "Interest", "Demo", ["Technology", "Finance"]),
  // Tech (1)
  D("osType", "OS Type", "Tech", ["ANDROID", "IOS", "MACOS", "WINDOWS"]),
  // Content (3) — Subreddit/Community/Keyword
  D("subreddit", "Subreddit", "Content", ["programming", "wallstreetbets"]),
  D("community", "Community", "Content", ["programming", "wallstreetbets"]),
  D("keyword", "Keyword", "Content", ["software engineering"]),
  // Creative / Placement (3)
  D("placement", "Placement", "Creative", ["FEED", "COMMENTS_PAGE"]),
  D("carouselCard", "Carousel Card", "Creative", ["card_1", "card_2"]),
  D(
    "galleryItemId",
    "Gallery Item",
    "Creative",
    ["gi_abc123"],
    /* deliveryOnlyWarning */ true
  ),
  // Conversion Attribution Windows (2)
  D("clickAttributionWindow", "Click Attribution Window", "Conversion", ["1d", "7d", "28d"]),
  D("viewAttributionWindow", "View Attribution Window", "Conversion", ["none", "1d"]),
];

// ============================================================================
// Closed-set field allowlist (single source of truth for valid wire-format fields)
// ============================================================================

/**
 * Set of all UPPERCASE Reddit field names valid in `body.fields[]`.
 * Derived at runtime from `REDDIT_ADS_METRICS[].apiField`.
 */
export const REDDIT_ADS_API_FIELDS: ReadonlySet<string> = new Set(
  REDDIT_ADS_METRICS.map((m) => m.apiField as string)
);

// O(1) lookup tables — built once at module load. The 159-entry catalogue
// is searched per-field per-row in `parseRedditMetric`; a linear scan would
// produce ~9.5M string comparisons for a typical 30-field × 2000-row response
// (Code Review Pass 3 finding — perf hardening).
const KEY_BY_API_FIELD = new Map<string, string>();
const API_FIELD_BY_KEY = new Map<string, string>();
for (const m of REDDIT_ADS_METRICS) {
  const apiField = m.apiField as string;
  KEY_BY_API_FIELD.set(apiField, m.key);
  API_FIELD_BY_KEY.set(m.key, apiField);
}

/**
 * Lookup helper: catalogue camelCase key → UPPERCASE Reddit field name.
 * O(1) via pre-built Map.
 */
export function getRedditAdsApiFieldByKey(camelCaseKey: string): string | undefined {
  return API_FIELD_BY_KEY.get(camelCaseKey);
}

/**
 * Lookup helper: UPPERCASE Reddit field name → catalogue camelCase key.
 * O(1) via pre-built Map.
 */
export function getRedditAdsKeyByApiField(apiField: string): string | undefined {
  return KEY_BY_API_FIELD.get(apiField);
}

/**
 * Validates a list of UPPERCASE Reddit field names against the catalogue's
 * `apiField` allowlist. Throws synchronously on the first unknown field.
 */
export function validateRedditAdsFieldList(
  fields: ReadonlyArray<string>
): void {
  for (const f of fields) {
    if (!REDDIT_ADS_API_FIELDS.has(f)) {
      throw new Error(`Reddit Ads field not in allowlist: ${f}`);
    }
  }
}
